import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { ValidationErrors } from 'webapp-types';

import setField from 'webapp/helpers/set-field';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormInputComponent from 'webapp/components/form/input';
import RowErrorComponent from 'webapp/components/row-error';

interface Args {
	label: string;
	password: string;
	onChange: (value: string) => void;
	isDisabled?: boolean;
	errors?: ValidationErrors<string>; // the form's errors; the password ones are shown
}

export default class FormPasswordWithCheckboxComponent extends Component<Args> {
	@tracked password = '';
	@tracked passwordRestrict = false;
	@tracked passwordFieldType = 'password';
	@tracked togglePasswordIconName: IconName = 'EyeOpen';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.args.password) {
			this.password = this.args.password;
			this.passwordRestrict = true;
		}
	}

	get passwordRestrictDisabled(): boolean {
		return !this.passwordRestrict;
	}

	get showPasswordToggle(): boolean {
		return !this.passwordRestrictDisabled;
	}

	get isCheckboxDisabled(): boolean {
		return !!this.args.isDisabled;
	}

	get isInputDisabled(): boolean {
		return !!this.args.isDisabled || this.passwordRestrictDisabled;
	}

	@action
	changePasswordRestrict(passwordRestrict: boolean): void {
		this.passwordRestrict = passwordRestrict;

		if (!passwordRestrict) {
			this.password = '';
			this.changePassword();
			this.passwordFieldType = 'password';
		}
	}

	@action
	toggleFieldType(): void {
		if (this.passwordFieldType === 'password') {
			this.passwordFieldType = 'text';
			this.togglePasswordIconName = 'EyeClosed';
		} else {
			this.passwordFieldType = 'password';
			this.togglePasswordIconName = 'EyeOpen';
		}
	}

	@action
	changePassword(): void {
		this.args.onChange(this.password);
	}
	<template>
		<div class="row password">
			<div class="checkbox">
				<FormCheckboxComponent
					@name="password"
					@selected={{this.passwordRestrict}}
					@label={{@label}}
					@isDisabled={{this.isCheckboxDisabled}}
					@onChange={{this.changePasswordRestrict}}
				/>
			</div>

			<div class="input checkbox-{{if this.passwordRestrict 'on' 'off'}}">
				<FormInputComponent
					@type={{this.passwordFieldType}}
					name="restrictWithPassword"
					disabled={{this.isInputDisabled}}
					placeholder="Enter password"
					@value={{this.password}}
					@focusOut={{this.changePassword}}
					@onEmpty={{setField target=this property="password"}}
					@hideFloatLabel={{true}}
					@loseFocusOnEnter={{true}}
				/>
				{{#if this.showPasswordToggle}}
					<a class="togglePass" role="button" {{on "click" this.toggleFieldType}}>
						<CommonUiIconComponent @name={{this.togglePasswordIconName}} @size="sm" />
					</a>
				{{/if}}
				<RowErrorComponent @items={{@errors.password}} />
			</div>
		</div>
	</template>
}
